import { useState, useEffect, useRef} from 'react';
import { useTheme } from '@mui/material/styles';
import Highcharts from 'highcharts'
import HighchartsReact from 'highcharts-react-official'
import 'highcharts/modules/accessibility';
import { Grid2 as Grid, Card, CardContent, Stack, FormControl, useMediaQuery, Box, Typography, colors} from '@mui/material';

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';


interface ChartDataCategory {
  name: string;
  data: number[]
}


const BostonElectionCharts = (): JSX.Element => {

    const theme = useTheme();
    const isSM = useMediaQuery(theme.breakpoints.down('sm'));
    const [startDate, setStartDate] = useState<Dayjs | null>(dayjs(new Date('2025-1-1')));
    const [endDate, setEndDate] = useState<Dayjs | null>(dayjs(new Date()));
    const [candidatesList, setCandidatesList] = useState<string[]>();
    const [totalContributions, setTotalContributions] = useState<Number[]>();
    const [totalExpenditures, setTotalExpenditures] = useState<Number[]>();
    const [totalBostonContributions, setTotalBostonContributions] = useState<Number[]>();
    const [totalElsewhereContributions, setTotalElsewhereContributions] = useState<Number[]>();
    const [amountbySizeData, setAmountbySizeData] = useState<ChartDataCategory [] | null>(null);  
    const [IESpendingData, setIESpendingData] = useState<ChartDataCategory [] | null>(null);

    const [isVisible, setIsVisible] = useState(false);
    const wholeChartRef = useRef();
    const IEChartRef = useRef<HighchartsReact.RefObject>(null);
    const [loadError, setLoadError] = useState<string | null>(null);


    useEffect(() => {

        //implement lazy loading to delay the API calls
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect(); // Stop observing once visible
                }
            },
            {
                threshold: 0.1, // Trigger when 10% of the component is visible
            }
        );

        if (wholeChartRef.current) {
            observer.observe(wholeChartRef.current);
        }

        return () => observer.disconnect();
    }, [])


    useEffect(() => {
        if(isVisible){
            const IEChart = IEChartRef.current && IEChartRef.current.chart;
            // Show loading for IE Chart as it takes longer to load; may need to optimize this later
            if (IEChart) {
                IEChart.showLoading('Loading data...');
            }
            getChartData();
        }
       
    }, [isVisible,startDate, endDate])


    const getChartData = () => {
        getTotalContributions();
        getTotalExpenditures();
        getBostonAndOtherContributions();
        getAmountbySizeData();
        getIESpendingData();
    }

    const getTotalContributions = async () => {

         try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/chartdata/electionChart?chartType=C-TOTAL&startDate=${startDate}&endDate=${endDate}`)
            .then(result => {
                if (!result.ok) {
                    setLoadError('Failed to get Boston Mayoral total contribution data.');
                    //showBoundary(new Error('Failed to get Boston Mayoral total contribution data.'));
                }
                return result.json();
            })
            .then(result => {
                let data = result;
                let total: Number[] = [];
                let candidates: string[] = [];
                data.forEach((item: any) => {
                    total.push(item.total);
                    candidates.push(item.filerName + ':' + item.cpfId);  
                })
                setTotalContributions(total);
                setCandidatesList(candidates);
            });
        } catch (error) {
            console.error('Uncaught Error:', error);
            setLoadError(`Failed to load total contributions data: ${error}`);
            //showBoundary(new Error(`${error}`));
        }
    }

    const getTotalExpenditures = async () => {

        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/chartdata/electionChart?chartType=E-TOTAL&startDate=${startDate}&endDate=${endDate}`)
            .then(result => {
                if (!result.ok) {
                    //showBoundary(new Error('Failed to get Boston Mayoral total expenditure data.'));
                    setLoadError('Failed to get Boston Mayoral total expenditure data.');
                }
                return result.json();
            })
            .then(result => {
                let data = result;
                let total: Number[] = [];
                data.forEach((item: any) => {
                    total.push(item.total);
                })
                setTotalExpenditures(total);
            });
        } catch (error) {
            console.error('Uncaught Error:', error);
            //showBoundary(new Error(`${error}`));
            setLoadError(`Failed to load total expenditures data: ${error}`);
        }
    }

    const getBostonAndOtherContributions = async () => {

        try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/chartdata/electionChart?chartType=C-RACE-CITY&startDate=${startDate}&endDate=${endDate}`)
            .then(result => {
                if (!result.ok) {
                    //showBoundary(new Error('Failed to get Boston Mayoral Boston VS Elsewhere data.'));
                    setLoadError('Failed to get Boston Mayoral Boston VS Elsewhere data.');
                }
                return result.json();
            })
            .then(result => {
                let data = result;
                let bostonTotal: Number[] = [];
                let elsewhereTotal: Number[] = [];
                data.filter((item: any) => item.category === 'Boston').forEach((item: any) => {
                    bostonTotal.push(item.count);
                });
                data.filter((item: any) => item.category === 'Elsewhere').forEach((item: any) => {
                    elsewhereTotal.push(item.count);
                });
                setTotalBostonContributions(bostonTotal);
                setTotalElsewhereContributions(elsewhereTotal);
            });
        } catch (error) {
            console.error('Uncaught Error:', error);
            //showBoundary(new Error(`${error}`));
            setLoadError(`Failed to load Boston and Elsewhere contributions data: ${error}`);
        }
    }

    const getAmountbySizeData = async () => {

         try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/chartdata/electionChart?chartType=C-RANGE&startDate=${startDate}&endDate=${endDate}`)
            .then(result => {
                if (!result.ok) {
                    //howBoundary(new Error('Failed to get Boston Mayoral Amount by Size data.'));
                    setLoadError('Failed to get Boston Mayoral Amount by Size data.');
                }
                return result.json();
            })
            .then(result => {
                let data = result;
                let categories: ChartDataCategory[] = [];
                data.forEach((item: any) => {
                    let category = item.category;
                    let categoryIndex = categories.findIndex((c) => c.name === category);
                    if (categoryIndex > -1){
                        categories[categoryIndex].data.push(item.total);
                    }else{
                        categories.push({
                            name: item.category,
                            data: [item.total]
                        });
                    }
                });
                setAmountbySizeData(categories);

        });
        } catch (error) {
            console.error('Uncaught Error:', error);
            //showBoundary(new Error(`${error}`));
            setLoadError(`Failed to load Amount by Size data: ${error}`);
        }
    }

    const getIESpendingData = async () => {

         try {
            await fetch(`${process.env.REACT_APP_API_BASE_URL}/chartdata/electionChart?chartType=E-IE&startDate=${startDate}&endDate=${endDate}`)
            .then(result => {
                if (!result.ok) {
                    //showBoundary(new Error('Failed to get Boston Mayoral Independent Spending data.'));
                    setLoadError('Failed to get Boston Mayoral Independent Spending data.');
                }
                return result.json();
            })
            .then(result => {
                let data = result;
                let categories: ChartDataCategory[] = [];
                data.forEach((item: any) => {
                    let category = item.category;
                    let categoryIndex = categories.findIndex((c) => c.name === category);
                    if (categoryIndex > -1){
                        categories[categoryIndex].data.push(item.total);
                    }else{
                        categories.push({
                            name: item.category,
                            data: [item.total]
                        });
                    }
                });
                setIESpendingData(categories);
                const IEChart = IEChartRef.current && IEChartRef.current.chart;
                // hide loading
                if (IEChart) {
                    IEChart.hideLoading();
                }
        });
        } catch (error) {
            console.error('Uncaught Error:', error);
            //showBoundary(new Error(`${error}`));
            setLoadError(`Failed to load Independent Spending data: ${error}`);
        }
    }
    

    const FundraisingChartOptions = {
        chart: {
            type: 'bar'
        },
        title: {
            text: 'Total Fundraising'
        },
        xAxis: {
            categories: candidatesList,
            title: {
                text: null
            },
            labels: {
              style: {
                  fontSize: '14px',
                  color: 'black',
              },
              formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
                  // @ts-ignore
                  return this.value.toString().split(":")[0]; // Display only the candidate name, not CPF ID
              }
            },
            gridLineWidth: 1,
            lineWidth: 0
        },
        yAxis: {
            min: 0,
            title: {
                text: null,
            },
            labels: {
                overflow: 'justify'
            },
            gridLineWidth: 0
        },
        tooltip: {
            valueDecimals: 2,
            valuePrefix: '$'
        },
        plotOptions: {
            bar: {
                    dataLabels: {
                        enabled: true,
                        formatter: function (this: Highcharts.Point): string {
                            return '$' + (this.y as number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                        },
                    },
                    groupPadding: 0.2,
                    pointPadding: 0,
                    cursor: 'pointer',
                    point: {
                        events: {
                            click: function (this: Highcharts.Point): void {
                            // Open the contributions report for the selected candidate
                                let cpfId = this.category?.toString().split(":")[1]; // Extract CPF ID from the category
                                window.open(`/Data/SearchItems?schedule=A&cpfId=${cpfId}`, '_blank'); 
                            }
                        }
                    }
            },
            series: {
                pointWidth: isSM ?20:40,
            }
        },
        legend: {
            align: 'center',
            verticalAlign: 'bottom',
            x: 0,
            y: 0,
            borderWidth: 1,
            backgroundColor: '#FFFFFF',
            shadow: true,
            reversed: false
        },
        credits: {
            enabled: false
        },
        accessibility: {
            enabled: true,
            description: 'A chart displaying total Raised and total Spent.',
            keyboardNavigation: {
                enabled: true
            }
        },
        colors: [colors.green[400], colors.orange[400]],
        series: [
            {
                name: 'Total Raised',
                data: totalContributions ? totalContributions : [0, 0],
            },
            {
                name: 'Total Spent',
                data: totalExpenditures ? totalExpenditures : [0, 0],
            }
        ]
    }

    const BostonVsOthersChartOptions = {
        chart: {
            type: 'bar'
        },
        title: {
            text: '<b>Counts of Contributions from Boston and Elsewhere</b>'
        },
        tooltip: {
            //valueDecimals: 2,
            //valuePrefix: '$'
            formatter: function (this: Highcharts.Point): string {
                return `${this.series.name}: <b>${this.y}</b> contributions`;
            }
        },
        xAxis: {
            categories: candidatesList,
            title: {
                text: null
            },
            labels: {
                style: {
                    fontSize: '14px',
                    color: 'black'
                },
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
                  // @ts-ignore
                  return this.value.toString().split(":")[0]; // Display only the candidate name, not CPF ID
              }
            },
            min: 0,
            lineWidth: 0,
            gridLineWidth: 0,
            lineColor: 'transparent',
            minorTickLength: 0,
            tickLength: 0
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Counts of Contributions',
                align: 'high'
            },
            labels: {
                overflow: 'justify'
            },
            lineWidth: 0,

            minorGridLineWidth: 0,
            lineColor: 'transparent'
        },
        plotOptions: {
            series: {
                stacking: 'normal'
            },
            bar: {
                dataLabels: {
                    enabled: true,
                    formatter: function (this: Highcharts.Point): string {
                        return Math.round(100 * (this.y as number) / (this.total as number)) + '%';
                    },
                    style: {
                        fontWeight: 'bold'
                    }
                },
                cursor: 'pointer',
                point: {
                    events: {
                            click: function (this: Highcharts.Point): void {
                            // Open the contributions report for the selected candidate
                                let cpfId = this.category?.toString().split(":")[1]; // Extract CPF ID from the categor
                                window.open(`/Data/SearchItems?schedule=A&cpfId=${cpfId}`, '_blank'); 
                            }
                    }
                }
            }
        },
        legend: {
            align: 'center',
            verticalAlign: 'bottom',
            x: 0,
            y: 0,
            borderWidth: 1,
            backgroundColor: '#FFFFFF',
            shadow: true,
            reversed: true
        },
        credits: {
            enabled: false
        },
        accessibility: {
            enabled: true,
            description: 'A chart displaying total Raised and total Spent.',
            keyboardNavigation: {
                enabled: true
            }
        },
        exporting: {
            enabled: false
        },
        colors: ['#b3d4ff','#00bfa0'],
        series: [
            {
                name: 'Elsewhere',
                data: totalElsewhereContributions ? totalElsewhereContributions : [0, 0],
            },
            {
                name: 'Boston',
                data: totalBostonContributions ? totalBostonContributions : [0, 0],
            } 
        ]
    }

    const CommitteeAndIESpentChartOptions = {
        chart: { 
            type: 'bar'
        },
        title: {
            text: '<b>Committee Spending VS Independent Spending</b>'
        },
        tooltip: {
            valueDecimals: 2,
            valuePrefix: '$'
        },
        xAxis: {
            categories: candidatesList,
            title: {
                text: null
            },
            labels: {
                style: {
                    fontSize: '14px',
                    color: 'black'
                },
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
                  // @ts-ignore
                  return this.value.toString().split(":")[0]; // Display only the candidate name, not CPF ID
              }
            },
            lineWidth: 0,
            gridLineWidth: 1,
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Total Expenditures',
                align: 'high'
            },
            labels: {
                overflow: 'justify'
            },
            gridLineWidth: 0,
        },
        plotOptions: {
            series: {
                //stacking: 'normal'
                pointWidth: isSM ? 18:21
            },
            bar: {
                dataLabels: {
                    enabled: true,
                    formatter: function (this: Highcharts.Point): string {
                            return '$' + (this.y as number).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                        },
                    style: {
                        fontWeight: 'bold'
                    }
                },
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            window.open(`/Reports/IndependentExpenditurePacReports`, '_blank');
                        }
                    }
                }
            }
        },
        legend: {
            align: 'center',
            verticalAlign: 'bottom',
            x: 0,
            y: 0,
            borderWidth: 1,
            backgroundColor: '#FFFFFF',
            shadow: true,
            reversed: false
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false
        },
        accessibility: {
            enabled: true,
            description: 'A chart displaying Committee Spending VS Independent Spending.',
            keyboardNavigation: {
                enabled: true
            }
        },
        colors: ['#24CBE5', '#50B432', '#ED561B'],
        series: IESpendingData
    }

    const ContributionsAmountSizeChartOptions = {
        chart: {
            type: 'bar',
        },
        title: {
            text: '<b>Percent of Money Raised by Contribution Size</b>'
        },
        tooltip: {
            valueDecimals: 2,
            valuePrefix: '$'
        },
        xAxis: {
            categories: candidatesList,
            title: {
                text: null
            },
            labels: {
                style: {
                    fontSize: '14px',
                    color: 'black'
                },
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject): string {
                  // @ts-ignore
                  return this.value.toString().split(":")[0]; // Display only the candidate name, not CPF ID
              }
            },
            min: 0,
            lineWidth: 0,
            gridLineWidth: 0,
            lineColor: 'transparent',
            minorTickLength: 0,
            tickLength: 0
        },
        yAxis: {
            min: 0,
            title: {
                text: 'Percentage',
                align: 'high'
            },
            stackLabels: {
                enabled: true,
                style: {
                    fontWeight: 'bold',
                    color: 'white'
                },
                formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
                    // @ts-ignore
                    return '$' + (this.total ? this.total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : '0');
                }
            },
            lineWidth: 0,
            minorGridLineWidth: 0,
            lineColor: 'transparent'
        },
        plotOptions: {
            series: {
                stacking: 'percent'
            },
            bar: {
                dataLabels: {
                    enabled: true,
                    formatter: function (this: Highcharts.Point) {
                        return Math.round(100 * (this.y as number) / (this.total as number)) + '%';
                    },
                    style: {
                        fontWeight: 'bold'
                    }
                },
                cursor: 'pointer',
                point: {
                    events: {
                            click: function (this: Highcharts.Point): void {
                            // Open the contributions report for the selected candidate
                                let cpfId = this.category?.toString().split(":")[1]; // Extract CPF ID from the categor
                                window.open(`/Data/SearchItems?schedule=A&cpfId=${cpfId}`, '_blank'); 
                            }
                    }
                }
            }
        },
        legend: {
            align: 'center',
            verticalAlign: 'bottom',
            x: 0,
            y: 0,
            borderWidth: 1,
            backgroundColor: '#FFFFFF',
            shadow: true,
            reversed: true
        },
        credits: {
            enabled: false
        },
        exporting: {
            enabled: false
        },
        accessibility: {
            enabled: true,
            description: 'A chart displaying Percent of Money Raised by Size.',
            keyboardNavigation: {
                enabled: true
            }
        },
        series: amountbySizeData
    }

    return (
    <Box marginBottom={4} ref={wholeChartRef}>
        <Box marginBottom={3}>
             <Typography
                variant="h6"
                sx={{
                        textTransform: 'uppercase',
                    }}
                fontWeight={700}
                gutterBottom
                align={'center'}
                color={'blue'}
            >
                2025 Boston Mayoral Race
            </Typography>
            <Stack marginTop={4} marginBottom={4} direction="row" spacing={1} alignItems={"center"} justifyContent="center">
                <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 120 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="Start Date"
                            slotProps={
                                {
                                    textField: { size: 'small' },
                                    field: { clearable: true, onClear: () => setStartDate(null) }
                                }
                            }
                            value={startDate}
                            onChange={(newValue) => setStartDate(newValue)}
                        />
                    </LocalizationProvider>
                </FormControl>
                <FormControl size="small" sx={{ m: 1, p: 0, minWidth: 120 }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker label="End Date"
                            slotProps={
                                      {
                                        textField: { size: 'small' },
                                        field: { clearable: true, onClear: () => setEndDate(null) }
                                      }
                            }
                            value={endDate}
                            onChange={(newValue) => setEndDate(newValue)}
                        />
                    </LocalizationProvider>
                </FormControl>
            </Stack>
            <Typography 
                align={'center'}
                justifyContent="center" 
                variant='body2' 
                color='text.secondary' 
                >
                    <i>* The candidate(s) on ballot need to meet the minimum threshold amount ($100,000) for display in the charts.</i>
            </Typography>
        </Box>
        {
            loadError ? (
                <Typography variant="body1" color="error" align="center">
                    {loadError}
                </Typography>
            ) : (
            <Grid container spacing={4}>
                {/* Fundraising Chart */}
                <Grid size={{xs:12, md:6}}>
                <Box
                    padding={0}
                    width={1}
                    component={Card}
                    boxShadow={2}
                >
                    <CardContent>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={FundraisingChartOptions}
                        />
                    </CardContent>   
                </Box>
                </Grid>
                {/* Committee and Independent Expenditures Chart */}
                <Grid size={{xs:12, md:6}}>
                <Box
                    padding={0}
                    width={1}
                    component={Card}
                    boxShadow={2}
                >
                    <CardContent>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={CommitteeAndIESpentChartOptions}
                            ref={IEChartRef}
                        />
                    </CardContent>   
                </Box>
                </Grid>
                {/* Boston vs Elsewhere Contributions Chart */}
                <Grid size={{xs:12, md:5}}>
                <Box
                    padding={0}
                    width={1}
                    component={Card}
                    boxShadow={2}
                >
                    <CardContent>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={BostonVsOthersChartOptions}
                        />
                    </CardContent>   
                </Box>
                </Grid>
                {/* Independent Expenditures Chart */}
                <Grid size={{xs:12, md:7}}>
                <Box
                    padding={0}
                    width={1}
                    component={Card}
                    boxShadow={2}
                >
                    <CardContent>
                        <HighchartsReact
                            highcharts={Highcharts}
                            options={ContributionsAmountSizeChartOptions}
                        />
                    </CardContent>   
                </Box>
                </Grid>
            </Grid>
            )
        }
    </Box>
    );
  };

export default BostonElectionCharts